import React, { useEffect, useRef, useState } from 'react';
import { noop, merge, map, throttle, orderBy } from 'lodash';
import { globalEventBus } from '@fiverr-private/futile';
import { bigQuery } from '@fiverr-private/obs';
import { CONTAINER_ID_ATTR, IMPRESSION_ITEM_ATTR, ITEM_WITHOUT_CONTAINER_SELECTOR } from '../useImpressionContainer/consts';
import { REGISTER_IMPRESSION_ITEM_EVENT, SEND_IMPRESSION_EVENT, MANAGER_ID, MANAGER_ELEMENT, getItemRegisteredInManagerEvent } from './consts';
import { calculateElementPositionInParent, calculateRelativeItemSize, getEmptyContainerObject } from './utils';

/**
 *
 * @param {Object} [globalEnrichment={}] - An object that will be added to each impression event.
 */
export const useImpressionManager = (globalEnrichment = {}) => {
    const eventBusRef = useRef(null);

    const [itemsMap] = useState({ [MANAGER_ID]: getEmptyContainerObject() });
    const pendingEvents = useRef([]);

    const flushAllPendingEvents = useRef(throttle(() => {
        let events = [...pendingEvents.current];
        pendingEvents.current = [];

        events = orderBy(events, ['payload.level']);

        events.forEach(({ payload, sendImpressionToBQ, onImpression }) => {
            sendImpressionToBQ && bigQuery.send(payload);
            onImpression(payload);
        });

    }, 100, { leading: false }));

    const sendRegisteredEventToItem = (itemId) => {
        globalEventBus.emit(getItemRegisteredInManagerEvent(itemId));
    };

    const sendRegisteredEventToTree = (rootContainerId) => {
        if (!rootContainerId) {
            return;
        }

        if (!itemsMap[rootContainerId]) {
            itemsMap[rootContainerId] = getEmptyContainerObject();
        }

        const itemIds = itemsMap[rootContainerId].items;
        itemsMap[rootContainerId].registered = true;

        itemIds.forEach(({ itemId, selfIdAsContainer }) => {
            sendRegisteredEventToItem(itemId);
            sendRegisteredEventToTree(selfIdAsContainer);
        });
    };

    const getItemIndexInContainer = ({ containerId, itemId }) => itemsMap[containerId]?.items?.findIndex((item) => item.itemId === itemId);

    const isManagerRegistered = () => (itemsMap[MANAGER_ID]?.registered);

    const handleRegisterItem = ({ itemId, containerId, selfIdAsContainer }) => {
        if (!containerId && !isManagerRegistered()) {
            return;
        }

        const key = containerId || MANAGER_ID;

        if (!itemsMap[key]) {
            itemsMap[key] = getEmptyContainerObject();
        }

        if (containerId) {
            Object.keys(itemsMap).forEach((containerKey) => {
                if (containerKey !== containerId) {
                    const itemIndexInContainer = getItemIndexInContainer({ containerId: containerKey, itemId });

                    if (itemIndexInContainer !== -1) {
                        itemsMap[containerKey].items.splice(itemIndexInContainer, 1);
                    }
                }
            });
        }

        itemsMap[key].items.push({ itemId, selfIdAsContainer });

        if (itemsMap[key]?.registered) {
            sendRegisteredEventToItem(itemId);
        }
    };

    const handleSendImpressionEvent = ({
        intersectionObserverEntry,
        itemId,
        containerId = null,
        containerRef = null,
        itemEnrichment = {},
        nestingLevel,
        isLeaf,
        isOverflowed,
        itemOffset = {},
        sendImpressionToBQ = true,
        onImpression = noop
    }) => {
        const positionInParent = calculateElementPositionInParent(containerId, itemId);
        const sizeInParent = calculateRelativeItemSize(intersectionObserverEntry.target, containerRef);

        const level = nestingLevel + 1;
        const containerEnrichment = {
            parent_component_imp_id: containerId,
            position_in_parent: positionInParent,
            level,
            is_leaf: isLeaf,
            size_in_parent: sizeInParent.x,
            visible_through_scroll: isOverflowed,
            location: {
                top_y_axis: itemOffset.top
            }
        };
        const payload = merge(containerEnrichment, itemEnrichment, globalEnrichment);

        pendingEvents.current.push({ payload, sendImpressionToBQ, onImpression });
        flushAllPendingEvents.current();
    };

    useEffect(() => {
        const items = MANAGER_ELEMENT.querySelectorAll(ITEM_WITHOUT_CONTAINER_SELECTOR);
        itemsMap[MANAGER_ID].items = map(items, (item) => {
            const selfIdAsContainer = item.getAttribute(CONTAINER_ID_ATTR);
            const itemId = item.getAttribute(IMPRESSION_ITEM_ATTR);

            sendRegisteredEventToItem(itemId);
            sendRegisteredEventToTree(selfIdAsContainer);

            return { itemId, selfIdAsContainer };
        });

        itemsMap[MANAGER_ID].registered = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => {
        globalEventBus.off(REGISTER_IMPRESSION_ITEM_EVENT, handleRegisterItem);
        globalEventBus.off(SEND_IMPRESSION_EVENT, handleSendImpressionEvent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!eventBusRef.current) {
        globalEventBus.on(REGISTER_IMPRESSION_ITEM_EVENT, handleRegisterItem);
        globalEventBus.on(SEND_IMPRESSION_EVENT, handleSendImpressionEvent);

        eventBusRef.current = true;
    }
};
